import { AppError } from './AppError';

/**
 * Error thrown when there is a conflict with the current state of a resource.
 */
export class ConflictError extends AppError {
  /**
   * Creates a new ConflictError instance.
   * @param message The error message.
   * @param resource The resource that has a conflict.
   * @param identifier The identifier of the resource.
   */
  constructor(
    message: string = 'Resource conflict',
    resource?: string,
    identifier?: string | number
  ) {
    const formattedMessage = resource && identifier
      ? `${message}: ${resource} with identifier ${identifier}`
      : message;
    
    super(formattedMessage, 'CONFLICT', true, { resource, identifier });
    this.name = 'ConflictError';
  }

  /**
   * Creates a ConflictError for a specific resource and identifier.
   * @param resource The resource that has a conflict.
   * @param identifier The identifier of the resource.
   * @returns A new ConflictError instance.
   */
  static forResource(resource: string, identifier: string | number): ConflictError {
    return new ConflictError(undefined, resource, identifier);
  }

  /**
   * Creates a ConflictError for a duplicate resource.
   * @param resource The resource that is a duplicate.
   * @param identifier The identifier of the resource.
   * @returns A new ConflictError instance.
   */
  static duplicate(resource: string, identifier: string | number): ConflictError {
    return new ConflictError(`Duplicate ${resource}`, resource, identifier);
  }

  /**
   * Creates a ConflictError for a resource that is already in use.
   * @param resource The resource that is already in use.
   * @param identifier The identifier of the resource.
   * @returns A new ConflictError instance.
   */
  static alreadyInUse(resource: string, identifier: string | number): ConflictError {
    return new ConflictError(`${resource} already in use`, resource, identifier);
  }
}